import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Cushion,
  FlexContainer,
  Rectangle,
  Shadow,
  Space,
  Typography,
} from '@pitchero/react-ui';
import { get, head } from 'lodash';
import { Trans } from '../../../lib/i18n';
import JoinClubPanel from './join-club-panel';
import ProductUnavailable from './product-unavailable';

const suggestedAmounts = [25, 50, 100, 150, 200];

const currencySymbols = {
  USD: '$', // US Dollar
  EUR: '€', // Euro
  CRC: '₡', // Costa Rican Colón
  GBP: '£', // British Pound Sterling
  ILS: '₪', // Israeli New Sheqel
  INR: '₹', // Indian Rupee
  JPY: '¥', // Japanese Yen
  KRW: '₩', // South Korean Won
  NGN: '₦', // Nigerian Naira
  PHP: '₱', // Philippine Peso
  PLN: 'zł', // Polish Zloty
  PYG: '₲', // Paraguayan Guarani
  THB: '฿', // Thai Baht
  UAH: '₴', // Ukrainian Hryvnia
  VND: '₫', // Vietnamese Dong
};

const minAmounts = {
  DKK: 250,
  HKD: 400,
  MXN: 1000,
  NOK: 300,
  PLN: 200,
  SEK: 300,
};

const currencyRegex = '^[0-9]*(.[0-9]{1,2})?$';

const ErrorMessage = ({ currencySymbol, error, maxAmount, minAmount }) => {
  if (!error) {
    return null;
  }

  return (
    <Typography color="negative" preset="description">
      <Trans
        i18nKey={`shop:donation_amount_error_${error}`}
        values={{
          max: `${currencySymbol}${(maxAmount / 100).toFixed(2)}`,
          min: `${currencySymbol}${(minAmount / 100).toFixed(2)}`,
        }}
      >
        {error}
      </Trans>
    </Typography>
  );
};

ErrorMessage.defaultProps = {
  error: null,
};

ErrorMessage.propTypes = {
  currencySymbol: PropTypes.string.isRequired,
  error: PropTypes.string,
  maxAmount: PropTypes.number.isRequired,
  minAmount: PropTypes.number.isRequired,
};

const DonationAmount = ({ addToBasket, isAddingToBasket, isLoggedIn, product }) => {
  const [amount, setAmount] = useState(suggestedAmounts[0].toFixed(2));
  const [panelOpen, setPanelOpen] = useState(false);
  const amountInt = parseInt(amount * 100, 10);

  const firstVariant = head(product.variants);
  const { id: firstVariantID, currencyCode } = firstVariant;
  const currencySymbol = get(currencySymbols, currencyCode, currencyCode);

  const handleClickBuy = () => {
    if (isLoggedIn) {
      addToBasket(firstVariantID, amountInt);
    } else {
      setPanelOpen(true);
    }
  };

  if (!product.canBePurchased) {
    return <ProductUnavailable />;
  }

  const minAmount = get(minAmounts, currencyCode, 100);
  const maxAmount = 100000000;

  const getAmountError = () => {
    if (amountInt === 0) {
      return null;
    }

    if (amountInt < minAmount) {
      return 'too_low';
    }
    if (amountInt >= maxAmount) {
      return 'too_high';
    }

    const regex = new RegExp(currencyRegex);
    if (!regex.exec(`${amount}`)) {
      return 'invalid';
    }

    return null;
  };

  const error = getAmountError();
  const invalidAmount = amountInt === 0 || !!error;

  return (
    <div>
      <Space bottom="small">
        <Typography preset="tag--large" color="dustygrey" component="h3">
          <Trans i18nKey="shop:donation_amount_label">Choose or enter an amount</Trans>
        </Typography>
      </Space>
      <Space bottom="medium">
        <Rectangle fill="mercury" radius={4}>
          <FlexContainer justifyContent="space-evenly">
            {suggestedAmounts.map((suggestedAmount, i) => {
              const isSelected = suggestedAmount === parseInt(amount, 10);
              const isLast = i === suggestedAmounts.length - 1;
              return (
                <Rectangle
                  key={suggestedAmount}
                  fill={isSelected ? 'white' : 'transparent'}
                  onClick={() => setAmount(suggestedAmount.toFixed(2))}
                  radius={isSelected ? 'rounded' : 0}
                  style={{ cursor: 'pointer', flex: 1 }}
                  borderRight={!isSelected && !isLast ? 'thinDarkGrey' : 'none'}
                >
                  <Cushion vertical="small">
                    <Typography preset="subtitle--large" align="center">
                      {`${currencySymbol}${suggestedAmount}`}
                    </Typography>
                  </Cushion>
                </Rectangle>
              );
            })}
          </FlexContainer>
        </Rectangle>
      </Space>
      <Space bottom="medium">
        <Shadow>
          <Rectangle fill="white" radius="rounded">
            <Cushion vertical="xsmall" horizontal="medium">
              <FlexContainer>
                <Space right="xsmall">
                  <Typography preset="subtitle--large" color="black" style={{ lineHeight: '38px' }}>
                    {currencySymbol}
                  </Typography>
                </Space>
                <Typography
                  key="donationAmount"
                  component="input"
                  preset="subtitle--large"
                  color="black"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  style={{ padding: 0, border: 0, flex: 1, outline: 0 }}
                  type="text"
                  inputMode="numeric"
                  pattern={currencyRegex}
                />
              </FlexContainer>
            </Cushion>
          </Rectangle>
        </Shadow>
        <ErrorMessage
          error={error}
          currencySymbol={currencySymbol}
          maxAmount={maxAmount}
          minAmount={minAmount}
        />
      </Space>

      <Button
        disabled={invalidAmount || isAddingToBasket}
        theme="primary"
        onClick={handleClickBuy}
        fullWidth
      >
        <Trans i18nKey="shop:donate">Donate</Trans>
      </Button>
      <JoinClubPanel
        isOpen={panelOpen}
        joinParams={{
          variant_id: firstVariantID,
          donation_amount: amountInt,
        }}
        addToBasket={() => addToBasket(firstVariantID, amountInt)}
        onClose={() => setPanelOpen(false)}
      />
    </div>
  );
};

DonationAmount.propTypes = {
  addToBasket: PropTypes.func.isRequired,
  isAddingToBasket: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    id: PropTypes.number,
    category: PropTypes.string,
    description: PropTypes.string,
    name: PropTypes.string,
    canBePurchased: PropTypes.bool,
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ),
  }).isRequired,
};

export default DonationAmount;
